/**
 * EvidenceOverviewPanel — the trust-signal panel.
 *
 * Sticky chip row showing which categories of evidence exist for ANY
 * research target. Each chip is clickable (jumps to that section via
 * anchor) and carries a tooltip definition of what that evidence type
 * actually means and its limitations.
 *
 * Reusable across explorers: works for vaccines, medicines, chemicals,
 * food additives, environmental exposure topics — anything with mixed
 * official / regulatory / peer-reviewed / surveillance / historical /
 * TCP-investigation evidence available.
 *
 * Props
 *   counts         { official_guidance, regulatory_document, peer_reviewed,
 *                    safety_surveillance, historical, tcp_investigation }
 *   anchorPrefix   Optional prefix for the section anchors (default "evidence-")
 */
import React from "react";
import { CheckCircle2, FileText, FlaskConical, AlertTriangle, Archive, BookOpen } from "lucide-react";

const CHIPS = [
    {
        key: "official_guidance",
        label: "Official guidance",
        icon: CheckCircle2,
        anchor: "official",
        tooltip: "Statements published by official health authorities (e.g. NHS, CDC). High trust for what the official position is — not necessarily what the science currently says.",
    },
    {
        key: "regulatory_document",
        label: "Regulatory documents",
        icon: FileText,
        anchor: "regulatory",
        tooltip: "Sources from regulators (MHRA, FDA, EMA) — SmPCs, PILs, approval documents. Reflect the position used to authorise the product.",
    },
    {
        key: "peer_reviewed",
        label: "Peer-reviewed evidence",
        icon: FlaskConical,
        anchor: "research",
        tooltip: "Studies published in peer-reviewed journals. Quality varies by study design — chips show study type so you can weigh them.",
    },
    {
        key: "safety_surveillance",
        label: "Safety surveillance",
        icon: AlertTriangle,
        anchor: "surveillance",
        tooltip: "Reports from monitoring systems like Yellow Card or VAERS. These detect potential signals — a reported event is NOT proof of causation.",
    },
    {
        key: "historical",
        label: "Historical documentation",
        icon: Archive,
        anchor: "history",
        tooltip: "Archived documents, schedule changes over time, decisions and the reasoning behind them.",
    },
    {
        key: "tcp_investigation",
        label: "TCP investigations",
        icon: BookOpen,
        anchor: "context",
        tooltip: "Investigations and analysis from The Context Project — connecting the topic to the wider archive.",
    },
];

export default function EvidenceOverviewPanel({ counts = {}, anchorPrefix = "evidence-", className = "" }) {
    const goto = (anchor) => {
        const el = document.getElementById(`${anchorPrefix}${anchor}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div
            className={`bg-[#111827] border border-[#243044] rounded-xl p-4 ${className}`}
            data-testid="evidence-overview-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold">Evidence available</h3>
                <span className="text-[10px] text-[#64748B]">Click a chip to jump to that section</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {CHIPS.map((c) => {
                    const count = counts[c.key] ?? 0;
                    const has = count > 0;
                    const Icon = c.icon;
                    return (
                        <button
                            key={c.key}
                            type="button"
                            onClick={() => has && goto(c.anchor)}
                            disabled={!has}
                            data-testid={`evidence-chip-${c.key}`}
                            title={c.tooltip}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium tracking-wide transition-colors ${
                                has
                                    ? "bg-[#0B1220] border border-[#56CCF2]/40 text-[#CBD5E1] hover:border-[#56CCF2] hover:text-white cursor-pointer"
                                    : "bg-[#0B1220] border border-[#243044]/40 text-[#475569] cursor-not-allowed line-through"
                            }`}
                        >
                            <Icon className="w-3 h-3" />
                            {c.label}
                            {has ? (
                                <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-[#56CCF2]/15 text-[#56CCF2] text-[10px] font-mono">{count}</span>
                            ) : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

EvidenceOverviewPanel.CHIPS = CHIPS;
